import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ShieldCheck, FileCheck, Camera, AlertTriangle, Loader2 } from "lucide-react";
import { API, useAuth } from "@/App";

export default function KYCGate() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [creating, setCreating] = useState(false);
  const [polling, setPolling] = useState(false);
  const [statusInfo, setStatusInfo] = useState(null);
  const [redirected, setRedirected] = useState(false);

  const isReturning = new URLSearchParams(location.search).get("return") === "1";

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/kyc/status`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setStatusInfo(data);
        if (data.kyc_verified) {
          await refreshUser();
          toast.success("Identity verified! Welcome to OurSpacez.");
          navigate("/dashboard", { replace: true });
        }
        return data;
      }
    } catch (e) {
      // silent
    }
    return null;
  }, [navigate, refreshUser]);

  // Auto-fetch on mount and when returning from Stripe
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Poll every 3s if returning from Stripe Identity (verification processing)
  useEffect(() => {
    if (!isReturning) return;
    setPolling(true);
    const interval = setInterval(async () => {
      const data = await fetchStatus();
      if (data?.kyc_verified || data?.status === "requires_input") {
        setPolling(false);
        clearInterval(interval);
      }
    }, 3000);
    const timeout = setTimeout(() => {
      setPolling(false);
      clearInterval(interval);
    }, 5 * 60 * 1000); // 5 min max
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isReturning, fetchStatus]);

  // If user is already KYC-verified, redirect immediately
  useEffect(() => {
    if (user?.kyc_verified && !redirected) {
      setRedirected(true);
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate, redirected]);

  const startVerification = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API}/kyc/create-verification-session`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || "Failed to start verification");
      }
      if (data.url) {
        // Redirect to Stripe-hosted verification flow
        window.location.href = data.url;
      } else if (data.status === "verified") {
        await refreshUser();
        navigate("/dashboard", { replace: true });
      }
    } catch (e) {
      toast.error(e.message || "Could not start verification");
    } finally {
      setCreating(false);
    }
  };

  const status = statusInfo?.status || "not_started";
  const lastError = statusInfo?.last_error;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a14] via-[#1a0a2e] to-[#0a0a14] py-12 px-4 flex items-center justify-center">
      <div className="max-w-2xl w-full">
        <div
          className="brutal-card relative overflow-hidden"
          style={{
            background: "linear-gradient(180deg, #fff 0%, #fff5fa 100%)",
            border: "4px solid #000",
            boxShadow: "8px 8px 0 #FF1493",
          }}
          data-testid="kyc-gate-card"
        >
          {/* Y2K corner sparkle */}
          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-pink-400 to-purple-600 opacity-20 rounded-bl-full" />

          <div className="relative p-8 md:p-10 text-black">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-black rounded-lg">
                <ShieldCheck className="w-8 h-8 text-pink-400" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-black tracking-tight text-black">
                  Verify Your Identity
                </h1>
                <p className="text-sm text-gray-600 font-medium">
                  Adults-only platform · Required to continue
                </p>
              </div>
            </div>

            {/* Status panels */}
            {polling && (
              <div
                className="mb-6 p-4 border-2 border-blue-500 bg-blue-50 rounded flex items-center gap-3"
                data-testid="kyc-processing-banner"
              >
                <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                <div>
                  <p className="font-bold text-blue-900">Verifying your documents…</p>
                  <p className="text-xs text-blue-700">
                    This usually takes a few seconds. We'll redirect you when it's done.
                  </p>
                </div>
              </div>
            )}

            {status === "requires_input" && lastError && (
              <div
                className="mb-6 p-4 border-2 border-red-500 bg-red-50 rounded flex items-start gap-3"
                data-testid="kyc-error-banner"
              >
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-red-900">Verification needs another try</p>
                  <p className="text-sm text-red-700">
                    {lastError.reason || lastError.code || "Please retry with a clearer photo."}
                  </p>
                </div>
              </div>
            )}

            <div className="mb-6 space-y-3">
              <p className="text-base text-gray-800 leading-relaxed">
                <strong>OurSpacez is strictly for adults.</strong> To keep our community safe,
                you'll need to verify your identity once with a government-issued ID and a
                quick selfie. The check is powered by Stripe Identity — we never see or store
                your documents.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
              <div className="p-4 border-2 border-black bg-yellow-100 rounded">
                <FileCheck className="w-6 h-6 mb-2 text-black" />
                <p className="font-bold text-sm text-black">Government ID</p>
                <p className="text-xs text-gray-700">Driver's license, passport, or ID card</p>
              </div>
              <div className="p-4 border-2 border-black bg-cyan-100 rounded">
                <Camera className="w-6 h-6 mb-2 text-black" />
                <p className="font-bold text-sm text-black">Quick Selfie</p>
                <p className="text-xs text-gray-700">Matches the photo on your ID</p>
              </div>
            </div>

            <Button
              onClick={startVerification}
              disabled={creating || polling}
              className="w-full text-lg font-black py-6 rounded-none"
              style={{
                background: "linear-gradient(90deg, #FF1493, #9333EA)",
                color: "#fff",
                border: "3px solid #000",
                boxShadow: "5px 5px 0 #000",
              }}
              data-testid="kyc-start-btn"
            >
              {creating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Opening Stripe Identity…
                </>
              ) : status === "requires_input" ? (
                "Try Verification Again"
              ) : (
                "Start Verification"
              )}
            </Button>

            <p className="text-xs text-center text-gray-500 mt-4">
              By continuing, you confirm you are 18+ and agree to identity verification.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
